import React from 'react';
import { GridColDef, GridRenderCellParams, GridRowModelUpdate } from "@mui/x-data-grid";
import { Box, Chip, Grid, TextField, Typography } from '@mui/material';
import FavoriteBorderIcon from '@material-ui/icons/FavoriteBorder';
import FavoriteIcon from '@material-ui/icons/Favorite';
import { LineupApiResponse } from '../../preContest/queries/queryTypes';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import { getCptOwnershipColumnName, getOwnershipColumnName, isInLineup } from '../../utils/showdownHandler';
import { Stack } from '@mui/material';
import { Projection } from '../EasyProjection/types';
import ReplayIcon from '@material-ui/icons/Replay';
import { isTeamSport } from '../../utils/teamSportHelper';
import { showStackColumnsPreContest } from '../../utils/stackConstants';
import { SlateResponse } from '../store/types';
import { getSuperflexSlotIndex } from './utils';
import { numberWithCommas } from '../../utils/stringUtils';
import ConditionalFormat from './ConditionalFormat';
import ConfirmedLineupIcons from './ConfirmedLineupIcons';
import StartingStatusIcons from './StartingStatusIcons';
import { CustomToolTip, toolTipText } from '../CustomToolTip';

interface PlayerGroup {
  id: string;
  players: LineupApiResponse[];
  roiBoost: number;
}

// Helper function to calculate group boost for a lineup
// For Showdown, CPT is always the first player in exportableLineup, rest are FLEX
const calculateGroupBoost = (lineup: LineupApiResponse, playerGroups: PlayerGroup[]): number => {
  let totalGroupBoost = 0;
  const lineupPlayerIds = lineup.exportableLineup.split(',').map(p => p.trim());
  const cptPlayerId = lineupPlayerIds[0]; // First player is CPT
  const flexPlayerIds = lineupPlayerIds.slice(1); // Rest are FLEX

  playerGroups.forEach(group => {
    if (group.players.length > 0 && group.roiBoost !== 0) {
      // Check if ALL players in the group are in this lineup (respecting CPT/FLEX position)
      const allPlayersInLineup = group.players.every(player => {
        const playerPos = player.position?.toUpperCase();
        if (playerPos === 'CPT') {
          return player.exportableNameAndId === cptPlayerId;
        } else if (playerPos === 'FLEX') {
          return flexPlayerIds.includes(player.exportableNameAndId);
        }
        // Non-showdown: just check if player is in lineup
        return lineupPlayerIds.includes(player.exportableNameAndId);
      });
      if (allPlayersInLineup) {
        totalGroupBoost += group.roiBoost;
      }
    }
  });

  return totalGroupBoost;
};

export const preContestLineupDataGridHeaderConfig = (setFavoriteLineup: (lineup: LineupApiResponse) => void, savedLineups: LineupApiResponse[], playerList: { [key: string]: number }, stackList: { [key: string]: number }, slate: SlateResponse, csvData: string, lockTier: boolean, playerGroups: PlayerGroup[] = []): GridColDef[] => {
  const sport = slate?.sport;
  const site = slate?.site?.toUpperCase();
  const superflexIndex = getSuperflexSlotIndex(slate);
  const includeStacks = showStackColumnsPreContest(slate);
  const coldef: GridColDef[] = [
    {
      field: 'simLineupRoi',
      headerName: 'Simulated ROI',
      cellClassName: 'conditionalFormatted',
      flex: .1,
      resizable: true,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueGetter(params) {
        const row: LineupApiResponse = params.api.getRowParams(params.id || 0)?.row;
        const playerBoostKeys = Object.keys(playerList);
        const stackBoostKeys = Object.keys(stackList);
        const boostKeys = [...playerBoostKeys, ...stackBoostKeys];
        const filteredBoostKeys = boostKeys.filter(x => isInLineup(row.exportableLineup, x, site, sport, superflexIndex) || row.stacks.find(y => ((sport.toLowerCase() === 'mlb' && y.count > 2) || (sport.toLowerCase() === 'nfl' && y.count > 0)) && y.team === x));
        var boostValue = 0;
        filteredBoostKeys.forEach(x => {
          boostValue = boostValue + (playerList[x] || 0) + (stackList[x] || 0)
        });

        // Add player group boosts
        const groupBoost = calculateGroupBoost(row, playerGroups);
        boostValue += groupBoost;

        if (isNaN(params.value)) {
          return null
        }
        const percentage = ((params.value + (boostValue / 100)));
        return percentage;
      },
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      // valueFormatter(params) {
      //   if (isNaN(params.value)) {
      //     return null
      //   }
      //   const row: LineupApiResponse = params.api.getRowParams(params.id || 0)?.row;
      //   const boostKeys = Object.keys(playerList);
      //   const filteredBoostKeys = boostKeys.filter(x => row.exportableLineup.includes(x));
      //   var boostValue = 0;

      //   filteredBoostKeys.forEach(x => {
      //     boostValue = boostValue + playerList[x]
      //   });
      //   console.log('params', params.value, boostValue);
      //   const percentage = (params.value * 100)?.toFixed(1);
      //   return `${percentage}% ${boostValue}%`;
      // },
      renderCell: (params: GridRenderCellParams<LineupApiResponse>) => {
        if (isNaN(params.value)) {
          return null
        }
        const row: LineupApiResponse = params.api.getRowParams(params.id || 0)?.row;
        const dataSet = Array.from(params.api.getRowModels(), ([name, value]) => ({ value })).map((x) => x.value.simLineupRoi);
        const playerBoostKeys = Object.keys(playerList);
        const stackBoostKeys = Object.keys(stackList);
        const boostKeys = [...playerBoostKeys, ...stackBoostKeys];
        const filteredBoostKeys = boostKeys.filter(x => isInLineup(row.exportableLineup, x, site, sport, superflexIndex) || row.stacks.find(y => ((sport.toLowerCase() === 'mlb' && y.count > 2) || (sport.toLowerCase() === 'nfl' && y.count > 0)) && y.team === x));
        var boostValue = 0;

        filteredBoostKeys.forEach(x => {
          boostValue = boostValue + (playerList[x] || 0) + (stackList[x] || 0)
        });

        // Add player group boosts
        const groupBoost = calculateGroupBoost(row, playerGroups);
        boostValue += groupBoost;

        const percentage = (params.value * 100)?.toFixed(1);
        return (
          <ConditionalFormat dataSet={dataSet} cellValue={params.value}>
            <Grid container justifyContent='center' style={{ fontWeight: 700 }}>
              <Grid item>
                {percentage}%
              </Grid>
              {
                boostValue !== 0 && (
                  <Grid item style={{
                    display: 'flex',
                    color: boostValue > 0 ? '#00BD08' : '#FF2525',
                  }} alignItems='center'>
                    {boostValue > 0 ? <ArrowUpwardIcon style={{ color: 'inherit' }} fontSize='small' /> : <ArrowDownwardIcon style={{ color: 'inherit' }} fontSize='small' />}
                    {boostValue}%
                  </Grid>
                )
              }
            </Grid>
          </ConditionalFormat>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.simulatedROI}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'simAverageFantasyPoints',
      headerName: 'Projected FP',
      flex: .1,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = params.value?.toFixed(2);
        return trim;
      },
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.projectedFPLineups}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'ownershipSum',
      headerName: 'OwnSum',
      flex: .1,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.ownSum}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'stacks',
      headerName: 'Stack',
      flex: .1,
      headerAlign: 'center',
      align: 'center',
      valueGetter(value,) {
        const teams = value.row.stacks?.map(x => x.team);
        if (sport?.toUpperCase() === 'NFL' && teams && teams.length > 0) {
          return teams.join('/')
        }
        return teams.join();
      },
      valueFormatter(params) {
        return params.value || '-';
      },
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
    },
    {
      field: 'simLineupRoiPct',
      headerName: 'Stack Type',
      flex: .1,
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      valueGetter(value,) {

        if (sport?.toUpperCase() === 'NFL') {
          const counts = value.row.stacks?.map(x => ({ count: x.count, rbCount: x.runbackCount }));
          if (counts && counts.length > 0) {
            return counts.map(x => x.rbCount > 0 ? `QB + ${x.count} | ${x.rbCount} OPP` : `QB + ${x.count}`).join("/")
          }
        }
        const counts2 = value.row.stacks?.map(x => x.count);
        return counts2?.join('-') || '-';
      },
      renderCell: (params: GridRenderCellParams<LineupApiResponse>) => {
        const row: LineupApiResponse = params.api.getRowParams(params.id || 0)?.row;
        const teams = row.stacks.map(x => x.count);
        if (sport?.toUpperCase() === 'NFL' && teams && teams.length > 0) {
          return (
            <Grid container justifyContent='center'>
              {params.value}
            </Grid>
          );
        }
        return (
          <Grid container justifyContent='center' style={{ fontWeight: 700, fontSize: '14px', }}>
            {teams?.join('-') || '-'}
          </Grid>
        )
      },
    },
    {
      field: 'win',
      headerName: 'Win%',
      hideable: true,
      flex: .1,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(3);
        return percentage + '%';
      },
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.winPercentage}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'top10',
      headerName: 'Top 10%',
      hideable: true,
      flex: .1,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(3);
        return percentage + '%';
      },
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.top10Percentage}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'cash',
      headerName: 'Cash%',
      hideable: true,
      flex: .1,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(3);
        return percentage + '%';
      },
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.cashPercentage}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'duplicates',
      headerName: 'Dupes',
      hideable: true,
      flex: .1,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      renderCell(params) {
        return (
          <Box fontWeight={500} fontSize='14px'>
            {params.formattedValue}
          </Box>
        )
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.dupes}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'stackInfo',
      headerName: 'Stack',
      hideable: true,
      flex: .1,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'fullLineup',
      headerName: 'Lineups',
      flex: .35,
      resizable: true,
      renderCell: (params: GridRenderCellParams<LineupApiResponse>) => {
        return (
          <Grid container justifyContent="space-between" style={{ padding: 2, fontSize: '16px', }}>
            <Grid item xs={12} style={{ whiteSpace: 'break-spaces', fontSize: '10px' }}>
              {params.value}
            </Grid>
          </Grid>
        )
      }
    },
    {
      field: 'salary',
      headerName: 'Salary',
      hideable: true,
      flex: .1,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        return '$' + params.value;
      },
      renderCell(params) {
        return (
          <Grid container justifyContent="space-between" style={{ padding: 2, fontSize: '16px', }}>
            <Grid item xs={10} style={{ whiteSpace: 'break-spaces', fontSize: '14px', fontWeight: 700, }}>
              {params.formattedValue}
            </Grid>
            <Grid container item xs={2} alignItems='center' justifyContent='center' onClick={() => setFavoriteLineup(params.row)}>
              {
                savedLineups.some((x => x.fullLineup === params.row.fullLineup)) ? (
                  <FavoriteIcon
                    style={{
                      cursor: 'pointer',
                      color: '#f44336',
                      maxWidth: 50,
                    }} />
                ) : (
                  <FavoriteBorderIcon
                    style={{
                      cursor: 'pointer',
                    }} />
                )
              }
            </Grid>
          </Grid>
        )
      },
    },
  ];

  let finalColumns = coldef;
  if (csvData.includes('\n') && !lockTier) {
    const headerData = csvData.split('\n')[0].split(',');
    const addlColumns = headerData.map((x, i) => {
      return {
        field: `HIDE${i}`,
        headerName: x,
        valueGetter(params) {
          const { exportableLineup } = params.row;
          return exportableLineup.split(",")[i];
        },
      } as GridColDef;
    });
    finalColumns = coldef.concat(addlColumns);
  }

  if (includeStacks === false) {
    finalColumns.splice(3, 2);
    if ((!isTeamSport(sport) || slate?.type?.toUpperCase() !== 'SHOWDOWN') && sport?.toUpperCase() !== 'MMA' && sport?.toUpperCase() !== 'NHL') {
      finalColumns.splice(7, 1);
    }
  } else {
    finalColumns.splice(9, 1);
  }

  return finalColumns;
}

export const preContestPlayerDataGridHeaderConfig = (includeTeam: boolean): GridColDef[] => {
  const headerConfig: GridColDef[] = [
    {
      field: 'player',
      headerName: 'Player',
      flex: .2,
      headerAlign: 'left',
      align: 'left',
      renderCell(params: GridRenderCellParams) {
        const name = params.value;
        return (
          <Box display="flex" justifyContent="center" alignItems="center">
            <Box fontSize='12px' fontWeight={700} style={{ textTransform: 'uppercase' }}>
              <span style={{ color: 'rgba(255, 255, 255, 0.70)' }}>{name?.split(' ')[0]}</span> <span style={{ fontSize: '14px', }}>{name?.split(' ')?.splice(1).join(' ')}</span>
            </Box>
          </Box>
        )
      },
    },
    {
      field: 'teamName',
      headerName: 'Team',
      flex: .2,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'opponentTeamName',
      headerName: 'Opponent',
      flex: .2,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'position',
      headerName: 'Position',
      flex: .2,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'salary',
      headerName: 'Salary',
      flex: .2,
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        return '$' + params.value;
      },
    },
    {
      field: 'simPlayerRoi',
      headerName: 'Avg Simulated ROI',
      flex: .2,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.avgSimulatedROI}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'projection',
      headerName: 'Projected FP',
      flex: .2,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = params.value?.toFixed(2);
        return trim;
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.projectedFP}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
  ];
  if (includeTeam === false) {
    headerConfig.splice(1, 2);
  }
  return headerConfig;
};

export const preContestFavoritedLineupDataGridHeaderConfig = (setFavoriteLineup: (lineup: LineupApiResponse) => void, savedLineups: LineupApiResponse[], includeStacks: boolean, sport: string): GridColDef[] => {
  const headerConfig: GridColDef[] = [
    {
      field: 'simLineupRoi',
      headerName: 'Simulated ROI',
      flex: .15,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.simulatedROI}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'simAverageFantasyPoints',
      headerName: 'Projected FP',
      flex: .15,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = params.value?.toFixed(2);
        return trim;
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.projectedFPLineups}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'ownershipSum',
      headerName: 'OwnSum',
      flex: .15,
      headerAlign: 'center',
      align: 'center',
      type: 'number',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.ownSum}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'stacks',
      headerName: 'Stack',
      flex: .1,
      headerAlign: 'center',
      align: 'center',
      valueGetter(value,) {
        const teams = value.row.stacks?.map(x => x.team);
        return teams.join();
      },
      valueFormatter(params) {
        return params.value || '-';
      },
    },
    {
      field: 'simLineupRoiPct',
      headerName: 'Stack Type',
      flex: .1,
      headerAlign: 'center',
      align: 'center',
      type: 'string',
      valueGetter(value,) {
        const counts = value.row.stacks?.map(x => x.count);
        return counts[0] || 0
      },
      renderCell: (params: GridRenderCellParams<LineupApiResponse>) => {
        const row: LineupApiResponse = params.api.getRowParams(params.id || 0)?.row;
        const teams = row.stacks.map(x => x.count);
        if (sport?.toUpperCase() === 'NFL' && teams && teams.length > 0) {
          return (
            <Grid container justifyContent='center' style={{ fontWeight: 700, fontSize: '14px' }}>
              {`QB + ${teams[0]}`}
            </Grid>
          );
        }
        return (
          <Grid container justifyContent='center' style={{ fontWeight: 700, fontSize: '14px' }}>
            {teams?.join('-') || '-'}
          </Grid>
        )
      },
    },
    {
      field: 'stackInfo',
      headerName: 'Stack',
      hideable: true,
      flex: .1,
      headerAlign: 'center',
      align: 'center',
      renderCell(params) {
        return (
          <Box fontWeight={700} fontSize='14px'>
            {params.value}
          </Box>
        )
      },
    },
    {
      field: 'fullLineup',
      headerName: 'Lineups',
      flex: .45,
      renderCell: (params: GridRenderCellParams<LineupApiResponse>) => {
        return (
          <Grid container justifyContent="space-between" style={{ padding: 2, fontSize: '16px', }}>
            <Grid item xs={12} style={{ whiteSpace: 'break-spaces', fontSize: '10px' }}>
              {params.value}
            </Grid>
          </Grid>
        )
      }
    },
    {
      field: 'salary',
      headerName: 'Salary',
      hideable: true,
      flex: .1,
      type: 'number',
      headerAlign: 'left',
      align: 'center',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        return '$' + params.value;
      },
      renderCell(params) {
        return (
          <Grid container justifyContent="space-between" style={{ padding: 2, fontSize: '16px', }}>
            <Grid item xs={10} style={{ whiteSpace: 'break-spaces', fontSize: '14px', fontWeight: 700 }}>
              {params.formattedValue}
            </Grid>
            <Grid container item xs={2} alignItems='center' justifyContent='center' onClick={() => setFavoriteLineup(params.row)}>
              {
                savedLineups.some((x => x.fullLineup === params.row.fullLineup)) ? (
                  <FavoriteIcon
                    style={{
                      cursor: 'pointer',
                      color: '#f44336',
                      maxWidth: 50,
                    }} />
                ) : (
                  <FavoriteBorderIcon
                    style={{
                      cursor: 'pointer',
                    }} />
                )
              }
            </Grid>
          </Grid>
        )
      },
    },
  ];

  if (includeStacks === false) {
    headerConfig.splice(3, 2);
    if (sport?.toUpperCase() !== 'MMA' && sport?.toUpperCase() !== 'NHL') {
      headerConfig.splice(3, 1);
    }
  }

  if (sport?.toUpperCase() === 'MLB') {
    const newHeader = headerConfig.filter(x => x.field !== 'stackInfo');
    return newHeader;
  }
  return headerConfig;
};

export const preContestStackExposuresDataGridHeaderConfig = (nfl: boolean, lineups: LineupApiResponse[]): GridColDef[] => {
  const qbNameColDef: GridColDef = {
    field: 'qbName',
    headerName: 'Quarterback',
    flex: 0.2,
    headerAlign: 'center',
    align: 'center',
  };

  const teamColDef: GridColDef = {
    field: 'team',
    headerName: 'Team',
    flex: 0.2,
    headerAlign: 'center',
    align: 'center',
  };

  const opponentTeamNameColDef: GridColDef = {
    field: 'opponentTeamName',
    headerName: 'Opponent',
    flex: 0.2,
    headerAlign: 'center',
    align: 'center',
  };

  const threeMLBOneNFLStackExposurePctColDef: GridColDef = {
    field: 'ThreeMLBOneNFLStackExposurePct',
    headerName: nfl ? 'QB + 1 WR/TE/RB' : '3 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const threePoolOwnColDef: GridColDef = {
    field: '3PoolOwn',
    headerName: '3 Stack Pool Own',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups.filter(x => x.stacks.find(y => y.count === 3 && y.team === params.row.team)).length;
      return stackCount / lineups.length;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const threePoolLevColDef: GridColDef = {
    field: '3PoolLev',
    headerName: '3 Stack Pool Leverage',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups.filter(x => x.stacks.find(y => y.count === 3 && y.team === params.row.team)).length;
      return params.row.ThreeMLBOneNFLStackExposurePct - (stackCount / lineups.length);
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const fourMLBTwoNFLStackExposurePctColDef: GridColDef = {
    field: 'FourMLBTwoNFLStackExposurePct',
    headerName: nfl ? 'QB + 2 WR/TE/RB' : '4 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const fourPoolOwnColDef: GridColDef = {
    field: '4PoolOwn',
    headerName: '4 Stack Pool Own',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups.filter(x => x.stacks.find(y => y.count === 4 && y.team === params.row.team)).length;
      return stackCount / lineups.length;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const fourPoolLevColDef: GridColDef = {
    field: '4PoolLev',
    headerName: '4 Stack Pool Leverage',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups.filter(x => x.stacks.find(y => y.count === 4 && y.team === params.row.team)).length;
      return params.row.FourMLBTwoNFLStackExposurePct - (stackCount / lineups.length);
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const fiveMLBThreeNFLStackExposurePctColDef: GridColDef = {
    field: 'FiveMLBThreeNFLStackExposurePct',
    headerName: nfl ? 'QB + 3 WR/TE/RB' : '5 Stack Exposures',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const fivePoolOwnColDef: GridColDef = {
    field: '5PoolOwn',
    headerName: '5 Stack Pool Own',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups.filter(x => x.stacks.find(y => y.count === 5 && y.team === params.row.team)).length;
      return stackCount / lineups.length;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const fivePoolLevColDef: GridColDef = {
    field: '5PoolLev',
    headerName: '5 Stack Pool Leverage',
    flex: 0.16,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueGetter(params) {
      const stackCount = lineups.filter(x => x.stacks.find(y => y.count === 5 && y.team === params.row.team)).length;
      return params.row.FiveMLBThreeNFLStackExposurePct - (stackCount / lineups.length);
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null;
      }
      if (params.value === 0) {
        return '-';
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  };

  const headerConfig: GridColDef[] = [
    teamColDef,
    opponentTeamNameColDef,
    threeMLBOneNFLStackExposurePctColDef,
    threePoolOwnColDef,
    threePoolLevColDef,
    fourMLBTwoNFLStackExposurePctColDef,
    fourPoolOwnColDef,
    fourPoolLevColDef,
    fiveMLBThreeNFLStackExposurePctColDef,
    fivePoolOwnColDef,
    fivePoolLevColDef,
  ];

  if (nfl) {
    return [qbNameColDef, teamColDef, threeMLBOneNFLStackExposurePctColDef, fourMLBTwoNFLStackExposurePctColDef, fiveMLBThreeNFLStackExposurePctColDef];
  }
  return headerConfig;
}

export const preContestExposuresDataGridHeaderConfig = (golf: boolean): GridColDef[] => {
  const headerConfig: GridColDef[] = [
    {
      field: 'player',
      headerName: 'Player',
      flex: .2,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'teamName',
      headerName: 'Team',
      flex: .2,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'opponentTeamName',
      headerName: 'Opponent',
      flex: .2,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'position',
      headerName: 'Position',
      flex: .16,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'salary',
      headerName: 'Salary',
      headerAlign: 'center',
      align: 'center',
      type: 'number',
    },
    {
      field: 'simPlayerRoi',
      headerName: 'Simulated Player ROI',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.simulatedPlayerROI}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'projection',
      headerName: 'Projected FP',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const trim = params.value?.toFixed(2);
        return trim;
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.projectedFP}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'overallOwnership',
      headerName: 'Pool Own',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value * 100)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.poolOwn}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'exposure',
      headerName: 'Exposure',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.exposure}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
    {
      field: 'leverage',
      headerName: 'Leverage',
      flex: .16,
      type: 'number',
      headerAlign: 'center',
      align: 'center',
      valueParser(value, params) {
        return value / 100;
      },
      valueFormatter(params) {
        if (isNaN(params.value)) {
          return null
        }
        const percentage = (params.value)?.toFixed(1);
        return percentage + '%';
      },
      renderHeader(params) {
        return (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <CustomToolTip title={toolTipText.leverage}>
              <Box>{params.colDef.headerName}</Box>
            </CustomToolTip>
          </Box>
        )
      },
    },
  ];

  if (golf) {
    headerConfig.splice(1, 2)
  }
  return headerConfig;
};

export const postContestStackDataGridHeaderConfig: GridColDef[] = [
  {
    field: 'teamName',
    headerName: 'Team',
    flex: .2,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'opponentTeamName',
    headerName: 'Opponent',
    flex: .2,
    headerAlign: 'center',
    align: 'center',
  },
  {
    field: 'count3Stack',
    headerName: '3 man Stack Count',
    flex: .2,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueFormatter(params) {
      if (isNaN(params.value) || params.value === 0) {
        return ''
      }
      return params.value;
    },
  },
  {
    field: 'simRoi3Man',
    headerName: '3 man Stack ROI',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  },
  {
    field: 'count4Stack',
    headerName: '4 man Stack Count',
    flex: .2,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueFormatter(params) {
      if (isNaN(params.value) || params.value === 0) {
        return ''
      }
      return params.value;
    },
  },
  {
    field: 'simRoi4Man',
    headerName: '4 man Stack ROI',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  },
  {
    field: 'count5Stack',
    headerName: '5 man Stack Count',
    flex: .2,
    headerAlign: 'center',
    align: 'center',
    type: 'number',
    valueFormatter(params) {
      if (isNaN(params.value) || params.value === 0) {
        return ''
      }
      return params.value;
    },
  },
  {
    field: 'simRoi5Man',
    headerName: '5 man Stack ROI',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  },
];

export const stackDataNFLGridHeaderConfig: GridColDef[] = [
  {
    field: 'qbName',
    headerName: 'Quarterback',
    flex: .2,
    headerAlign: 'left',
  },
  {
    field: 'teamName',
    headerName: 'Team',
    flex: .2,
    headerAlign: 'left',
  },
  {
    field: 'qB1FlexSimulatedRoi',
    headerName: 'QB + 1',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  },
  {
    field: 'qB2FlexSimulatedRoi',
    headerName: 'QB + 2',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  },
  {
    field: 'qB3FlexSimulatedRoi',
    headerName: 'QB + 3',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(1);
      return percentage + '%';
    },
  },
];


export const projectionsHeaderConfig = (originalProjections: Projection[], projections: Projection[], updateSessionStorage: (newProjections: Projection[]) => void, slate: SlateResponse, disableCustomization: boolean, useLiveProjections?: boolean, isMobile?: boolean): GridColDef[] => {
  const sport = slate?.sport?.toUpperCase();
  const site = slate?.site?.toUpperCase();
  const showdown = slate?.type?.toUpperCase() === 'SHOWDOWN';

  // Mobile-enhanced Player column with Name + Position + Team vs Opponent stacked
  const mobileNameColumn: GridColDef = {
    field: 'name',
    headerName: 'Player',
    width: 150,
    minWidth: 150,
    align: 'left',
    headerAlign: 'left',
    renderCell(params: GridRenderCellParams<Projection>) {
      const { name, position, team, teamLogo, opponent, confirmedLineup, lineupPosition, starting, injuryStatus } = params.row;
      return (
        <Stack sx={{ overflow: 'hidden', width: '100%', py: 0.5 }}>
          {/* Player name row */}
          <Box display="flex" alignItems="center">
            <Box fontSize='13px' fontWeight={500} sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              textTransform: 'uppercase'
            }}>
              <span>{name?.split(' ')[0]}</span> {name?.split(' ')?.splice(1).join(' ')}
            </Box>
            {slate?.sport?.toLowerCase() === 'mlb' && (
              <Box paddingLeft='4px'>
                <ConfirmedLineupIcons confirmed={confirmedLineup} battingOrder={lineupPosition} />
              </Box>
            )}
          </Box>
          {/* Position + Team vs Opponent row */}
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
            {position && (
              <Chip
                label={position}
                variant='filled'
                size='small'
                sx={{
                  fontSize: '10px',
                  color: 'rgba(255, 255, 255, 0.70)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  px: 0.5,
                  py: 0,
                  borderRadius: '4px',
                  '& .MuiChip-label': {
                    padding: '0px',
                  },
                }}
              />
            )}
            {teamLogo && (
              <Box height="14px" width="14px">
                <img src={teamLogo} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
              </Box>
            )}
            <Box sx={{ fontSize: '11px', color: 'rgba(255, 255, 255, 0.6)' }}>
              {team}{opponent ? ` vs ${opponent}` : ''}
            </Box>
            {/* Starting/Injury icons for non-MLB */}
            {(starting !== undefined || (injuryStatus && injuryStatus !== 'None' && injuryStatus !== 'Unknown')) && slate?.sport?.toLowerCase() !== 'mlb' && (
              <StartingStatusIcons starting={starting} confirmedLineup={confirmedLineup} injuryStatus={injuryStatus} />
            )}
          </Stack>
        </Stack>
      )
    },
  };

  // Desktop Player column (original)
  const name: GridColDef = {
    field: 'name',
    headerName: 'Player',
    flex: .3,
    align: 'left',
    headerAlign: 'left',
    renderCell(params: GridRenderCellParams<Projection>) {
      const { name, confirmedLineup, lineupPosition, starting, injuryStatus } = params.row;
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Box fontSize='12px' fontWeight={700} style={{ textTransform: 'uppercase' }}>
            <span style={{ color: 'rgba(255, 255, 255, 0.70)' }}>{name?.split(' ')[0]}</span> {name?.split(' ')?.splice(1).join(' ')}
          </Box>
          {
            slate?.sport?.toLowerCase() === 'mlb' && (
              <Box paddingLeft='4px'>
                <ConfirmedLineupIcons confirmed={confirmedLineup} battingOrder={lineupPosition} />
              </Box>
            )
          }
          {
            (starting !== undefined || (injuryStatus && injuryStatus !== 'None' && injuryStatus !== 'Unknown')) && slate?.sport?.toLowerCase() !== 'mlb' && (
              <Box paddingLeft='4px'>
                <StartingStatusIcons starting={starting} confirmedLineup={confirmedLineup} injuryStatus={injuryStatus} />
              </Box>
            )
          }
        </Box>
      )
    },
  };
  const salary: GridColDef = {
    field: 'salary',
    headerName: 'Salary',
    flex: .1,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      return Math.round(params.value);
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return null
      }
      return <Typography variant="caption" fontSize='14px' fontWeight={500}>${numberWithCommas(params.value)}</Typography>;
    },
  };
  const position: GridColDef = {
    field: 'position',
    headerName: 'Position',
    flex: .1,
    headerAlign: 'center',
    align: 'center',
  };
  const team: GridColDef = {
    field: 'team',
    headerName: 'Team',
    flex: .1,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      const row = params.row;
      return (
        <Stack direction="row">
          {row.teamLogo &&
            <Box height="20px" width="20px" marginRight="4px" fontWeight={500}>
              <img src={row.teamLogo} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
            </Box>
          }
          {params.value}
        </Stack>
      )
    }
  };
  const opponent: GridColDef = {
    field: 'opponent',
    headerName: 'Opponent',
    flex: .1,
    headerAlign: 'left',
    align: 'left',
    renderCell: (params: GridRenderCellParams<Projection>) => {
      const row = params.row;
      return (
        <Stack direction="row">
          {row.opponentLogo &&
            <Box height="20px" width="20px" marginRight="4px" fontWeight={500}>
              <img src={row.opponentLogo} alt="" style={{ height: '100%', width: '100%', objectFit: 'contain' }} />
            </Box>
          }
          {params.value}
        </Stack>
      )
    }
  };
  const projection: GridColDef = {
    field: 'projection',
    headerName: disableCustomization ? 'Projection' : 'Projected FP',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? params.row.projection?.toFixed(2) : Cell(params, originalProjections, projections, updateSessionStorage),
    renderHeader(params) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          {isMobile ? (
            <Stack alignItems='center'>
              <Typography variant="caption" fontWeight={700} fontSize='12px'>FP</Typography>
              <Typography variant="caption" fontSize='9px'>Proj</Typography>
            </Stack>
          ) : (
            <CustomToolTip title={toolTipText.projectedFP}>
              <Box>Projected FP</Box>
            </CustomToolTip>
          )}
        </Box>
      )
    },
  };
  const variance: GridColDef = {
    field: 'variance',
    headerName: 'Variance',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? params.row.projection?.toFixed(2) : VarianceCell(params, originalProjections, projections, updateSessionStorage),
    renderHeader(params) {
      return (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <CustomToolTip title={toolTipText.variance}>
            <Box>Variance</Box>
          </CustomToolTip>
        </Box>
      )
    },
  };

  const currentPoints: GridColDef = {
    field: 'currentPoints',
    headerName: 'Current Points',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      const value = params.value?.toFixed(2);
      return value;
    },
  };

  const currentMinutes: GridColDef = {
    field: 'currentMinutes',
    headerName: 'Current Minutes',
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      const value = params.value?.toFixed(2);
      return value;
    },
  };

  const projectedBattingOrder: GridColDef = {
    field: 'lineupPosition',
    headerName: 'Bat Pos.',
    description: 'Projected Batting Position',
    flex: .08,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? params.row.lineupPosition?.toFixed(0) : LineupPositionCell(params, originalProjections, projections, updateSessionStorage),
  };
  const hrPercentCol: GridColDef = {
    field: 'hrPercent',
    headerName: 'HR %',
    flex: .12,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    renderCell(params) {
      if (params.value == null || isNaN(params.value)) return null;
      return (params.value * 100)?.toFixed(1) + '%';
    },
  };
  const topPitcherPercentCol: GridColDef = {
    field: 'topPitcherPercent',
    headerName: 'Top Pitcher %',
    flex: .14,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    renderCell(params) {
      if (params.value == null || isNaN(params.value)) return null;
      return (params.value * 100)?.toFixed(1) + '%';
    },
  };
  const topValuePercentCol: GridColDef = {
    field: 'topValuePercent',
    headerName: 'Top Value %',
    flex: .14,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    renderCell(params) {
      if (params.value == null || isNaN(params.value)) return null;
      return (params.value * 100)?.toFixed(1) + '%';
    },
  };
  const handCol: GridColDef = {
    field: 'hand',
    headerName: 'Hand',
    flex: .08,
    type: 'string',
    headerAlign: 'left',
    align: 'left',
  };
  const confirmedCol: GridColDef = {
    field: 'confirmedLineup',
    headerName: 'Confirmed',
    flex: .08,
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (params.value === true) return 'C';
      if (params.value === false) return 'P';
      return '';
    },
  };
  const postponedCol: GridColDef = {
    field: 'postponed',
    headerName: 'PPD',
    flex: .1,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    renderCell(params) {
      if (params.value == null || isNaN(params.value)) return null;
      return (params.value * 100)?.toFixed(1) + '%';
    },
  };
  const projectedTeamTotalCol: GridColDef = {
    field: 'projectedTeamTotal',
    headerName: 'Team Total',
    flex: .12,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueGetter(params) {
      const pos = params.row?.position;
      if (pos === 'P' || pos === 'SP' || pos === 'RP') return null;
      return params.value;
    },
    renderCell(params) {
      if (params.value == null || isNaN(params.value)) return null;
      return params.value?.toFixed(2);
    },
  };
  const value: GridColDef = {
    field: 'value',
    headerName: 'Value',
    flex: .1,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return '0'
      }
      const value = params.value?.toFixed(2);
      return value;
    },
    renderHeader(params) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center">
          { isMobile ? (
            <Stack alignItems='center'>
              <Typography variant="caption" fontWeight={700} fontSize='10px'>Val</Typography>
              <Typography variant="caption" fontSize='9px'>Pts/$</Typography>
            </Stack>
          ) : (
            <CustomToolTip title={toolTipText.value}>
              <Box>Value</Box>
            </CustomToolTip>
          )}
        </Box>
      )
    },
  };

  const ownership: GridColDef = {
    field: 'ownership',
    headerName: getOwnershipColumnName(slate, disableCustomization),
    flex: .16,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? (params.value ? (params.value * 100)?.toFixed(2) + '%' : '0%') : OwnershipCell(params, originalProjections, projections, updateSessionStorage, (proj: Projection | undefined) => proj?.ownership, updateOwnership),
    renderHeader(params) {
      return (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <CustomToolTip title={toolTipText.ownershipPercentage}>
            <Box>{params.colDef.headerName}</Box>
          </CustomToolTip>
        </Box>
      )
    },
  };
  const updateOwnership = (projection: Projection, value: number) => {
    return { ...projection, ownership: value < 0 ? 0 : value / 100 } as GridRowModelUpdate;
  }
  const optimal: GridColDef = {
    field: 'optimal',
    headerName: 'Optimal %',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const leverage: GridColDef = {
    field: 'leverage',
    headerName: 'Leverage',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const cptOwnership: GridColDef = {
    field: 'cptOwnership',
    headerName: getCptOwnershipColumnName(slate, disableCustomization),
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? (params.value ? (params.value * 100)?.toFixed(2) + '%' : '0%') : OwnershipCell(params, originalProjections, projections, updateSessionStorage, (proj: Projection | undefined) => proj?.cptOwnership, updateCptOwnership),
  };
  const updateCptOwnership = (projection: Projection, value: number) => {
    return { ...projection, cptOwnership: value < 0 ? 0 : value / 100 } as GridRowModelUpdate;
  }
  const cptOptimal: GridColDef = {
    field: 'cptOptimal',
    headerName: sport === 'NBA' && site === 'FD' ? 'MVP Optimal %' : 'CPT Optimal %',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const cptLeverage: GridColDef = {
    field: 'cptLeverage',
    headerName: sport === 'NBA' && site === 'FD' ? 'MVP Leverage' : 'CPT Leverage',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const starOwnership: GridColDef = {
    field: 'starOwnership',
    headerName: disableCustomization ? 'Star Ownership %' : 'Projected Star Ownership',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? (params.value ? (params.value * 100)?.toFixed(2) + '%' : '0%') : OwnershipCell(params, originalProjections, projections, updateSessionStorage, (proj: Projection | undefined) => proj?.starOwnership, updateStarOwnership),
  };
  const updateStarOwnership = (projection: Projection, value: number) => {
    return { ...projection, starOwnership: value < 0 ? 0 : value / 100 } as GridRowModelUpdate;
  }
  const starOptimal: GridColDef = {
    field: 'starOptimal',
    headerName: 'Star Optimal %',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const starLeverage: GridColDef = {
    field: 'starLeverage',
    headerName: 'Star Leverage',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const proOwnership: GridColDef = {
    field: 'proOwnership',
    headerName: disableCustomization ? 'Pro Ownership %' : 'Projected Pro Ownership',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value * 100;
    },
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? (params.value ? (params.value * 100)?.toFixed(2) + '%' : '0%') : OwnershipCell(params, originalProjections, projections, updateSessionStorage, (proj: Projection | undefined) => proj?.proOwnership, updateProOwnership),
  };
  const updateProOwnership = (projection: Projection, value: number) => {
    return { ...projection, proOwnership: value < 0 ? 0 : value / 100 } as GridRowModelUpdate;
  }
  const proOptimal: GridColDef = {
    field: 'proOptimal',
    headerName: 'Pro Optimal %',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const proLeverage: GridColDef = {
    field: 'proLeverage',
    headerName: 'Pro Leverage',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return 0
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return '0%'
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const stdDev: GridColDef = {
    field: 'stdDev',
    headerName: 'Std Dev',
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      return params.value?.toFixed(2);
    },
  };
  const floor: GridColDef = {
    field: 'floor',
    headerName: 'Floor',
    flex: .1,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      return params.value?.toFixed(2);
    },
  };
  const ceiling: GridColDef = {
    field: 'ceiling',
    headerName: 'Ceiling',
    flex: .1,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      if (isNaN(params.value)) {
        return null
      }
      return params.value?.toFixed(2);
    },
  };
  const winPct: GridColDef = {
    field: 'winPercent',
    headerName: 'Win %',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const top6Pct: GridColDef = {
    field: 'top6Percent',
    headerName: 'Top 6 %',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };
  const makeTheCutPct: GridColDef = {
    field: 'makeTheCutPercent',
    headerName: 'Make Cut %',
    flex: .16,
    type: 'number',
    headerAlign: 'left',
    align: 'left',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };

  const minutes: GridColDef = {
    field: 'minutes',
    headerName: 'Mins',
    flex: .1,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? params.row.minutes?.toFixed(2) : MinutesCell(params, originalProjections, projections, updateSessionStorage),
  }

  const fppm: GridColDef = {
    field: 'fppm',
    headerName: 'FPPM',
    flex: .1,
    type: 'number',
    headerAlign: 'center',
    description: 'Fantasy Points Per Minute',
    align: 'center',
    renderCell: (params: GridRenderCellParams<Projection>) => disableCustomization ? params.row.fppm?.toFixed(2) : FppmCell(params, originalProjections, projections, updateSessionStorage),
  }

  const boom: GridColDef = {
    field: 'boom',
    headerName: 'Boom',
    flex: .14,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      return params.value * 100;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  }
  const bust: GridColDef = {
    field: 'bust',
    headerName: 'Bust',
    flex: .1,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      return params.value * 100;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  }

  // Injury Status column (hidden by default via HIDE suffix)
  const injuryStatusCol: GridColDef = {
    field: 'injuryStatusHIDE',
    headerName: 'Injury',
    flex: .08,
    headerAlign: 'center',
    align: 'center',
    hideable: true,
    valueGetter: (params) => params.row.injuryStatus,
    renderCell(params) {
      const status = params.row.injuryStatus?.toLowerCase();
      if (!status || status === 'none' || status === 'unknown') {
        return null;
      }
      // Show single letter: Q, P, D, O, G
      return status.charAt(0).toUpperCase();
    },
  };

  // Starting column (hidden by default via HIDE suffix)
  const startingCol: GridColDef = {
    field: 'startingHIDE',
    headerName: 'Starting',
    flex: .10,
    headerAlign: 'center',
    align: 'center',
    hideable: true,
    valueGetter: (params) => params.row.starting,
    renderCell(params: GridRenderCellParams<Projection>) {
      const { starting, confirmedLineup } = params.row;
      if (!starting) {
        return null;
      }
      return confirmedLineup ? 'Yes' : 'Probable';
    },
  };

  // NHL-specific columns
  const line: GridColDef = {
    field: 'line',
    headerName: 'Line',
    flex: .08,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
  };

  const powerPlay: GridColDef = {
    field: 'pp',
    headerName: 'Power Play',
    flex: .1,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
  };

  // MMA-specific columns
  const fightNumberCol: GridColDef = {
    field: 'lineupPosition',
    headerName: 'Fight #',
    flex: .06,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (params.value == null) return null;
      return params.value?.toFixed(0);
    },
  };

  const fightLengthCol: GridColDef = {
    field: 'fightLength',
    headerName: 'Rounds',
    flex: .06,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
  };

  const projectedFightTotalCol: GridColDef = {
    field: 'projectedFightTotal',
    headerName: 'Fight Total',
    flex: .08,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (params.value == null) return null;
      return params.value?.toFixed(1);
    },
  };

  const koWinPercentCol: GridColDef = {
    field: 'koWinPercent',
    headerName: 'WID %',
    flex: .07,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (params.value == null) return null;
      return (params.value * 100)?.toFixed(1) + '%';
    },
  };

  const paceCol: GridColDef = {
    field: 'pace',
    headerName: 'Pace',
    flex: .06,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (params.value == null) return null;
      return params.value?.toFixed(2);
    },
  };

  const greaterThan100PercentCol: GridColDef = {
    field: 'greaterThan100Percent',
    headerName: '>100 Fpts %',
    flex: .08,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueFormatter(params) {
      if (params.value == null) return null;
      return (params.value * 100)?.toFixed(2) + '%';
    },
  };

  const mmaWinPctCol: GridColDef = {
    field: 'winPercent',
    headerName: 'Win %',
    flex: .07,
    type: 'number',
    headerAlign: 'center',
    align: 'center',
    valueParser(value, params) {
      return value / 100;
    },
    valueFormatter(params) {
      if (isNaN(params.value)) {
        return null
      }
      return params.value;
    },
    renderCell(params) {
      if (isNaN(params.value)) {
        return null
      }
      const percentage = (params.value * 100)?.toFixed(2);
      return percentage + '%';
    },
  };

  let headers: GridColDef[] = [
    isMobile ? mobileNameColumn : name,
    salary,
    position,
    team,
    opponent,
    minutes,
    fppm,
    projection,
    value,
    ownership,
    optimal,
    leverage,
    proOwnership,
    proOptimal,
    proLeverage,
    starOwnership,
    starOptimal,
    starLeverage,
    cptOwnership,
    cptOptimal,
    cptLeverage,
    stdDev,
    variance,
    boom,
    bust,
    ceiling,
    floor
  ];

  // On mobile, remove opponent column (they're shown in the Player cell)
  // Keep position column for filtering (hidden via columnVisibilityModel in newTable.tsx)
  if (isMobile) {
    headers = headers.filter(x => x.field !== 'opponent');
    // Convert flex to fixed widths for mobile
    headers = headers.map(col => {
      if (col.field === 'name') return col; // Already has fixed width
      return {
        ...col,
        flex: undefined,
        width: col.type === 'number' ? 70 : 80,
        minWidth: col.type === 'number' ? 70 : 80,
      };
    });
  }

  if (useLiveProjections) {
    headers.splice(8, 0, currentPoints, currentMinutes);
  }
  if (!isTeamSport(sport)) {
    headers = headers.filter(x => x.field !== 'team' && x.field !== 'position' && x.field !== 'opponent'); // Remove team, position, and opponent columns for non-team sports
  }
  if (!showdown) {
    headers = headers.filter(x => x.field !== 'cptOwnership' && x.field !== 'cptOptimal' && x.field !== 'cptLeverage' && x.field !== 'starOwnership' && x.field !== 'starOptimal' && x.field !== 'starLeverage' && x.field !== 'proOwnership' && x.field !== 'proOptimal' && x.field !== 'proLeverage'); // Remove captain, star, and pro columns for non-showdown sports
  }
  if (showdown) {
    if (sport === 'MLB' && site === 'FD') {
      headers = headers.filter(x => x.field !== 'proOwnership' && x.field !== 'proOptimal' && x.field !== 'proLeverage'); // Remove star, and pro columns for non-FD showdown sports
    }
    else {
      headers = headers.filter(x => x.field !== 'starOwnership' && x.field !== 'starOptimal' && x.field !== 'starLeverage' && x.field !== 'proOwnership' && x.field !== 'proOptimal' && x.field !== 'proLeverage'); // Remove star, and pro columns for non-FD showdown sports
    }
  }
  if (sport === 'PGA' && disableCustomization) {
    headers.push(makeTheCutPct, top6Pct, winPct); // Add make cut %, top 6% and win % columns for PGA at the end
    if (projections.filter(x => x.winPercent !== undefined)?.length === 0) {
      headers = headers.filter(x => x.field !== 'winPercent') // Remove win % column for PGA
    }
    if (projections.filter(x => x.top6Percent !== undefined)?.length === 0) {
      headers = headers.filter(x => x.field !== 'top6Percent') // Remove top 6 % column for PGA
    }
    if (projections.filter(x => x.makeTheCutPercent !== undefined)?.length === 0) {
      headers = headers.filter(x => x.field !== 'makeTheCutPercent') // Remove make cut % column for PGA
    }
  }
  if (sport === 'MLB') {
    headers.splice(3, 0, projectedBattingOrder); // Add projected batting order for MLB
    headers.push(handCol, confirmedCol, projectedTeamTotalCol, postponedCol, hrPercentCol, topPitcherPercentCol, topValuePercentCol); // Add MLB-specific columns
  }
  if (sport === 'NHL') {
    headers.push(line, powerPlay); // Add line and power play columns for NHL at the end
  }
  if ((disableCustomization && originalProjections.filter(x => x.ownership !== undefined).length === 0)) {
    headers = headers.filter(x => !x.field.toLowerCase().includes('ownership') && !x.field.toLowerCase().includes('leverage') && !x.field.toLowerCase().includes('optimal')); // Remove ownership columns if it doesn't exist in the original projections
  }
  if (sport !== 'NBA') {
    headers = headers.filter(x => x.field !== 'minutes' && x.field !== 'fppm' && x.field !== 'variance');
  }
  if (sport === 'NBA') {
    headers.push(injuryStatusCol, startingCol); // Add injury status and starting columns for NBA
  }
  if (sport === 'MMA') {
    // Add MMA-specific columns at the end
    headers.push(projectedFightTotalCol, paceCol, fightNumberCol, fightLengthCol, mmaWinPctCol, koWinPercentCol, greaterThan100PercentCol);
  }
  if (disableCustomization) {
    headers = headers.filter(x => x.field !== 'variance');
  }
  else {
    // Hide optimal and leverage in edit mode
    headers = headers.filter(x => x.field !== 'optimal' && x.field !== 'leverage');
  }

  // Hide columns when no data contains that field
  const dataToCheck = originalProjections.length > 0 ? originalProjections : projections;

  if (dataToCheck.length > 0) {
    const fieldsToCheck = ['boom', 'bust', 'ceiling', 'floor', 'stdDev', 'variance', 'minutes', 'fppm', 'winPercent', 'top6Percent', 'makeTheCutPercent', 'optimal', 'leverage', 'cptOptimal', 'cptLeverage', 'hrPercent', 'topPitcherPercent', 'topValuePercent', 'hand', 'postponed', 'projectedTeamTotal'];
    fieldsToCheck.forEach(field => {
      const hasData = dataToCheck.some(row => row[field] !== undefined && row[field] !== null);
      if (!hasData) {
        headers = headers.filter(x => x.field !== field);
      }
    });
  }

  return headers;
}

const MinusIcon = (props: any): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
      <rect width="16" height="16" rx="8" fill="#DF0019" fillOpacity="0.15" />
      <path d="M3.33337 8.5V7.5H12.6667V8.5H3.33337Z" fill="#DF0019" />
    </svg>
  )
}
const PlusIcon = (props: any): JSX.Element => {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
      <rect width="16" height="16" rx="8" fill="#00D008" fillOpacity="0.15" />
      <path d="M7.50004 12.6666V8.49992H3.33337V7.49992H7.50004V3.33325H8.50004V7.49992H12.6667V8.49992H8.50004V12.6666H7.50004Z" fill="#00D008" />
    </svg>
  )
}

const Cell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void) => {
  const [textFieldValue, setTextFieldValue] = React.useState(params.value?.toFixed(2));

  const handleClick = (value: number) => {
    params.api.updateRows([{ ...params.row, projection: value < 0 ? 0 : value, fppm: value / params.row.minutes }])
    setTextFieldValue(value < 0 ? 0 : value?.toFixed(2));
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }
  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);

    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: original?.projection
    };

    const isGreater = parseFloat(params.value?.toFixed(2)) > parseFloat((original?.projection?.toFixed(2) || '0'));
    const isLess = parseFloat(params.value?.toFixed(2)) < parseFloat((original?.projection?.toFixed(2) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }

  const OGChanges = getOGProjChange();

  React.useEffect(() => {
    setTextFieldValue(params?.value?.toFixed(2))
  }, [projections, params.value])

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value - 1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          type='tel'
          size='small'
          InputProps={{
            endAdornment: OGChanges.value?.toFixed(2) !== params.value?.toFixed(2) && <ReplayIcon fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick(OGChanges.value || 0)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value + 1)} ><PlusIcon /></Stack>
    </Stack>
  )
}
const VarianceCell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void) => {
  const [textFieldValue, setTextFieldValue] = React.useState(params.value?.toFixed(2));

  const handleClick = (value: number) => {
    params.api.updateRows([{ ...params.row, variance: value < 0 ? 0 : value }])
    setTextFieldValue(value < 0 ? 0 : value?.toFixed(2));
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }
  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);

    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: original?.variance
    };

    const isGreater = parseFloat(params.value?.toFixed(2)) > parseFloat((original?.variance?.toFixed(2) || '0'));
    const isLess = parseFloat(params.value?.toFixed(2)) < parseFloat((original?.variance?.toFixed(2) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }

  const OGChanges = getOGProjChange();

  React.useEffect(() => {
    setTextFieldValue(params?.value?.toFixed(2))
  }, [projections, params.value])

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value - .1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          type='tel'
          size='small'
          InputProps={{
            endAdornment: OGChanges.value?.toFixed(2) !== params.value?.toFixed(2) && <ReplayIcon fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick(OGChanges.value || 0)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value + .1)} ><PlusIcon /></Stack>
    </Stack>
  )
}

const FppmCell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void) => {
  const [textFieldValue, setTextFieldValue] = React.useState(params.value?.toFixed(2));

  const handleClick = async (value: number) => {
    params.api.updateRows([{ ...params.row, fppm: value, projection: value * params.row.minutes }])
    setTextFieldValue(value < 0 ? 0 : value.toFixed(2));
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }
  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);

    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: original?.fppm
    };

    const isGreater = parseFloat(params.value?.toFixed(0)) > parseFloat((original?.fppm?.toFixed(0) || '0'));
    const isLess = parseFloat(params.value?.toFixed(0)) < parseFloat((original?.fppm?.toFixed(0) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }

  const OGChanges = getOGProjChange();

  React.useEffect(() => {
    setTextFieldValue(params.value?.toFixed(2))
  }, [projections, params.value])

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value - .1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          type='tel'
          size='small'
          InputProps={{
            endAdornment: OGChanges.value?.toFixed(2) !== params.value?.toFixed(2) && <ReplayIcon fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick(OGChanges.value || 0)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value + .1)} ><PlusIcon /></Stack>
    </Stack>
  )
}
const MinutesCell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void) => {
  const [textFieldValue, setTextFieldValue] = React.useState(params.value?.toFixed(2));

  const handleClick = async (value: number) => {
    params.api.updateRows([{ ...params.row, minutes: value, projection: value * params.row.fppm }])
    setTextFieldValue(value < 0 ? 0 : value.toFixed(2));
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }
  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);

    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: original?.minutes
    };

    const isGreater = parseFloat(params.value?.toFixed(0)) > parseFloat((original?.minutes?.toFixed(0) || '0'));
    const isLess = parseFloat(params.value?.toFixed(0)) < parseFloat((original?.minutes?.toFixed(0) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }

  const OGChanges = getOGProjChange();

  React.useEffect(() => {
    setTextFieldValue(params.value?.toFixed(2))
  }, [projections])

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value - 1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          type='tel'
          size='small'
          InputProps={{
            endAdornment: OGChanges.value?.toFixed(2) !== params.value?.toFixed(2) && <ReplayIcon fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick(OGChanges.value || 0)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value + 1)} ><PlusIcon /></Stack>
    </Stack>
  )
}

const LineupPositionCell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void) => {
  const [textFieldValue, setTextFieldValue] = React.useState(params.value?.toFixed(2));

  const handleClick = (value: number) => {
    params.api.updateRows([{ ...params.row, lineupPosition: value < 0 ? 0 : value }])
    setTextFieldValue(value < 0 ? 0 : value?.toFixed(0));
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }
  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);

    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: original?.lineupPosition
    };

    const isGreater = parseFloat(params.value?.toFixed(0)) > parseFloat((original?.lineupPosition?.toFixed(0) || '0'));
    const isLess = parseFloat(params.value?.toFixed(0)) < parseFloat((original?.lineupPosition?.toFixed(0) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }

  const OGChanges = getOGProjChange();

  React.useEffect(() => {
    setTextFieldValue(params.value?.toFixed(0))
  }, [projections, params.value])

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" sx={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value - 1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          type='tel'
          size='small'
          InputProps={{
            endAdornment: OGChanges.value?.toFixed(2) !== params.value?.toFixed(2) && <ReplayIcon fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick(OGChanges.value || 0)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(params.value + 1)} ><PlusIcon /></Stack>
    </Stack>
  )
}

const OwnershipCell = (params: GridRenderCellParams<Projection>, originalProjections: Projection[], projections: Projection[], updateSessionStorage: (projections: Projection[]) => void, getOwnership: (proj: Projection | undefined) => number | undefined, getRowUpdate: (projection: Projection, value: number) => GridRowModelUpdate) => {
  const value = params.value ? params.value : 0;
  const [textFieldValue, setTextFieldValue] = React.useState(((value * 100)?.toFixed(2) + '%'));
  const handleClick = (value: number) => {
    params.api.updateRows([getRowUpdate(params.row, value)])
    setTextFieldValue(value < 0 ? '0%' : value?.toFixed(2) + '%')
    const data = params.api.state.rows.dataRowIds.map(x => params.api.getRowParams(x).row)
    updateSessionStorage(data as Projection[]);
  }

  const getOGProjChange = () => {
    const original = originalProjections.find(x => x.nameAndId === params.row.nameAndId);
    const own = getOwnership(original);
    let styling = {
      background: '',
      border: '1px solid rgba(255, 255, 255, 0.15)',
      value: own?.toFixed(2),
      floatValue: own
    };

    const isGreater = parseFloat(value?.toFixed(2)) > parseFloat((own?.toFixed(2) || '0'));
    const isLess = parseFloat(value?.toFixed(2)) < parseFloat((own?.toFixed(2) || '0'));
    if (isGreater) {
      styling = {
        ...styling,
        background: '#003402',
        border: '1px solid rgba(0, 229, 9, 0.30)',
      }
    }
    if (isLess) {
      styling = {
        ...styling,
        background: '#3A0000',
        border: '1px solid rgba(206, 17, 38, 0.30)',
      }
    }

    return styling;
  }

  const OGChanges = getOGProjChange();

  React.useEffect(() => {
    setTextFieldValue((value * 100)?.toFixed(2) + '%')
  }, [projections])

  return (
    <Stack direction="row" width="100%" justifyContent="space-around" style={{
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '6px',
      padding: '4px',
    }}>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick((value ?? 0) * 100 - 1)}><MinusIcon /></Stack>
      <Stack justifyContent="center" alignItems="center" borderRadius="4px" border={OGChanges.border} width="84px" sx={{ '& .MuiInputBase-root': { color: 'white' }, '& .MuiInputBase-input': { textAlign: 'center', padding: 0, fontSize: '14px', height: 'auto' } }}>
        <TextField
          value={textFieldValue}
          onChange={(e) => setTextFieldValue(e.target.value)}
          onBlur={(e) => handleClick(parseFloat(e.target.value))}
          size='small'
          InputProps={{
            endAdornment: OGChanges.value !== params.value?.toFixed(2) && <ReplayIcon fontSize='inherit' style={{ cursor: 'pointer', padding: '2px' }} onClick={() => handleClick((OGChanges.floatValue || 0) * 100)} />
          }}
        />
      </Stack>
      <Stack justifyContent="center" style={{ cursor: 'pointer' }} onClick={() => handleClick(value * 100 + 1)}><PlusIcon /></Stack>
    </Stack>
  )
}
